import { useContext, useEffect, useMemo, useState } from 'react'

import {
  GenderOptionControl,
  theme,
  useHealthToolsCache,
} from '@hhgtech/hhg-components'
import { BabyGrowthChooseProfileDrawer } from '@hhgtech/hhg-components/babyGrowth'
import {
  Button,
  DatePicker,
  Input,
  Select,
  Text,
} from '@hhgtech/hhg-components/mantine'
import {
  AdditionalQuestionResponseType,
  AnswerType,
  BabyVaccinationDetail,
  BabyVaccinationDetailApi,
  QuestionResponseType,
} from '@hhgtech/hhg-components/types'
import { formatBabyVaccinationDetail } from '@hhgtech/hhg-components/vaccination'
import { Flex, rem, SelectItem } from '@mantine/core'
import { useForm, yupResolver } from '@mantine/form'
import { useDisclosure } from '@mantine/hooks'
import dayjs from 'dayjs'
import Cookies from 'js-cookie'
import isEmpty from 'lodash/isEmpty'
import { useRouter } from 'next/dist/client/router'
import queryString from 'query-string'
import { useIntl } from 'react-intl'

import { PATHS } from 'api/paths'
import { callApiWithAuth } from 'api/utils'
import {
  getBabyVaccineToolPath,
  // getHelloSitesUrl
} from 'api/utils/paths'
import { redirectToSSOWithReturnUrl } from 'api/utils/redirects'
// import { Option } from 'components/molecules/dropdownSearch'
import { AdditionalQuestionField } from 'components/atoms/healthTools/form/additionalQuestionField'
import { AgeInput } from 'components/atoms/healthTools/form/ageInput'
import { CheckYourSelfSwitching } from 'components/atoms/healthTools/form/checkYourSelfSwitching'
import { showNotification } from 'components/molecules/notificationsHandler'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { API_DATE_FORMAT, basePath, ENV } from 'interfaces/constants'
import { TEMP_BABY_GROWTH_KEY } from 'interfaces/constants/cookies'
import {
  ADDITIONAL_QUESTION_PREFIX,
  HEALTH_TOOL_TYPE,
} from 'interfaces/constants/healthTools'
import { ERROR_MESSAGE } from 'interfaces/error-message'
import { Gender, LOCALE } from 'interfaces/types'
import { AppContext } from 'state/appContext'
import { BABY_GROWTH_ACTION } from 'state/babyGrowth/actions'
import { BabyGrowthContext } from 'state/babyGrowth/context'
import { BABY_VACCINATION_ACTION } from 'state/babyVaccination/actions'
import { bmiCalculate } from 'utils/bmiCalculate'
import { checkAndSaveUserInfo } from 'utils/healthTools'

import { Baby } from './baby'
import { generateGender, generateSchema } from './helper'
import {
  StyledBabyGrowthForm,
  StyledBabyGrowthWrapper,
  StyledFormControl,
  StyledRow,
} from './index.styled'

type BabyGrowthFormProps = Record<string, string>
export type BabyGrowthFormField = {
  name?: string
  isMyself: boolean
  gender: Gender
  birthday: Date
  height: number
  weight: number
  head: number
  bmi: number
  measurementDate: Date
  isSubmitted?: boolean
  parentBirthday?: string
}

type BabyOption = {
  additionalQuestions: BabyVaccinationDetail['additionalQuestions']
} & BabyVaccinationDetail['profile']

const BabyGrowthForm = ({}: BabyGrowthFormProps) => {
  const currentUrl = useCurrentUrl()
  const { query, asPath } = useRouter()
  const source = query.source
  const isUserReturnFromSSO = source && source.includes('baby-growth')

  const {
    state: { babyLists, babyGrowthContent },
    dispatch,
  } = useContext(BabyGrowthContext)
  const {
    state: { isLoggedIn, userInfo },
  } = useContext(AppContext)

  const isMobile = useIsMobile()
  const { formatMessage: f } = useIntl()
  const {
    locale,
    router: { push },
  } = useRouterExtend()
  const { getHealthToolLink } = useHealthToolsCache(locale)
  const [opened, { open, close }] = useDisclosure(false)
  const [isBlocked, setIsBlocked] = useState(false)
  const [searchValue, onSearchChange] = useState('')
  const [selectedBaby, setSelectedBaby] = useState<BabyOption | null>(null)
  const additionalQuestions = babyGrowthContent?.listQuestions || []

  useEffect(() => {
    if (isLoggedIn) open()
  }, [isLoggedIn])

  const schema = useMemo(
    () =>
      generateSchema(
        f,
        additionalQuestions.map(
          (i) => ADDITIONAL_QUESTION_PREFIX + i.question.id,
        ),
      ),
    [additionalQuestions],
  )
  const form = useForm<any>({
    initialValues: {
      isMyself: false,
      measurementDate: new Date(),
      isSubmitted: false,
    },
    validate: yupResolver(schema),
  })

  const weight = form.values.weight
  const height = form.values.height
  const head = form.values.head

  const isEmptyThreeFields = useMemo(
    () =>
      form.values.isSubmitted &&
      ((!weight && !height && !head) ||
        (weight === 0 && height === 0 && head === 0)),
    [form.values.isSubmitted, weight, height, head],
  )

  useEffect(() => {
    if (isEmptyThreeFields)
      showNotification({
        type: 'danger',
        message: f({
          id:
            ENV.DEPLOY_LOCALE === LOCALE.Vietnam
              ? ERROR_MESSAGE.BABY_GROWTH.AT_LEAST_ONE_OF_VN
              : ERROR_MESSAGE.BABY_GROWTH.AT_LEAST_ONE_OF,
          defaultMessage:
            'Must have at least one of these fields: Weight, Height, Head Circumference (Head).',
        }),
        title: f({
          id: 'babyGrowth.error.title',
          defaultMessage: 'Invalid data',
        }),
      })
  }, [isEmptyThreeFields])

  useEffect(() => {
    const bmi = bmiCalculate(height, weight)
    form.setFieldValue('bmi', bmi)
  }, [weight, height])

  const babyListOptions = useMemo<Array<SelectItem & BabyOption>>(
    () =>
      (babyLists || []).map((baby) => {
        return {
          label: baby.name,
          value: baby.name,
          ...baby,
        }
      }),
    [babyLists],
  )

  useEffect(() => {
    ;(async () => {
      try {
        if (isLoggedIn) {
          const babyInfo = await callApiWithAuth(
            getBabyVaccineToolPath(
              PATHS.BABY_VACCINE.GET_HEALTH_TOOL_BABY_VACCINE_LIST,
            ),
            'GET',
            {},
          )
          if (babyInfo?.data) {
            dispatch({
              type: BABY_GROWTH_ACTION.UPDATE_BABY_LIST,
              payload: (babyInfo?.data.items || []).map(
                (baby: BabyVaccinationDetailApi) => {
                  const formatData = formatBabyVaccinationDetail(baby)
                  return {
                    ...formatData.profile,
                    additionalQuestions: formatData.additionalQuestions,
                  }
                },
              ),
            })
          }
        }
      } catch {}
    })()
    form.setFieldValue('isMyself', isLoggedIn)
  }, [isLoggedIn])
  const onSubmit = async (value: typeof form.values) => {
    form.setFieldValue('isSubmitted', true)
    if (value.weight === 0 && value.height === 0 && value.head === 0) return
    if (isLoggedIn) {
      form.validateField('parentBirthday')
    }
    Cookies.set(
      TEMP_BABY_GROWTH_KEY,
      JSON.stringify({
        ...value,
        ...(isLoggedIn && {
          parentBirthday:
            form.getInputProps('parentBirthday').value || userInfo?.birthday,
        }),
      }),
    )

    if (!isLoggedIn) {
      // Force log in
      redirectToSSOWithReturnUrl(
        queryString.stringifyUrl({
          url: `${window.location.origin}${basePath}${getHealthToolLink(
            HEALTH_TOOL_TYPE.BABY_GROWTH,
          )}`,
          query: {
            joincommunity: 1,
          },
        }),
        'baby-growth',
      )
      return
    }
    const payload = { ...value, name: selectedBaby?.name || value.name || '' }
    const newResponseList = [...(selectedBaby?.additionalQuestions || [])]
    additionalQuestions.map((i) => {
      const indexOfQuestion = newResponseList.findIndex(
        (j) => j.question.id === i.question.id,
      )
      const curSelectedAnswer: string[] = (
        value[ADDITIONAL_QUESTION_PREFIX + i.question.id] || ''
      )
        .split(',')
        .filter(Boolean)
      const listAnswerByIds: { [key in string]: AnswerType } = {}
      i.answers.map((i: AnswerType) => {
        listAnswerByIds[i.id] = i
      })
      const listAnswer: QuestionResponseType[] = []
      const otherVal: string[] = []
      curSelectedAnswer
        .map((j: string) => {
          if (listAnswerByIds[j]) {
            listAnswer.push({
              id: listAnswerByIds[j].id,
              value: listAnswerByIds[j].answer,
            })
          } else {
            otherVal.push(j)
          }
        })
        .filter(Boolean)
      const answers = [
        ...listAnswer,
        ...(otherVal.length > 0
          ? [
              {
                id: 0,
                value: otherVal?.[0],
              },
            ]
          : []),
      ].filter(Boolean)
      const response: AdditionalQuestionResponseType = {
        question: {
          id: Number(i.question.id),
          value: i.question.question,
        },
        answers,
      }
      if (indexOfQuestion !== -1) {
        if (response.answers.length > 0) {
          newResponseList[indexOfQuestion] = response
        } else {
          newResponseList.splice(indexOfQuestion, 1)
        }
      } else {
        response.answers.length > 0 && newResponseList.push(response)
      }
    })
    const [toolRes] = await Promise.all([
      callApiWithAuth(
        getBabyVaccineToolPath(
          PATHS.BABY_VACCINE.GET_HEALTH_TOOL_BABY_VACCINE_LIST,
        ),
        'POST',
        {
          data: {
            // 0 if user "really" wanna add new baby, 1 is for us to save temporary data on SIDIS
            type: Number(!Boolean(value.isMyself)),
            is_myself: value.isMyself,
            name: value.name || 'a',
            gender: value.gender === Gender.Boy ? true : false,
            dob: dayjs(value.birthday).format(API_DATE_FORMAT),
            ...(newResponseList.length > 0 && {
              additional_questions: newResponseList,
            }),
          },
        },
      ),
      userInfo?.id &&
        value?.parentBirthday &&
        (await checkAndSaveUserInfo({
          userInfo,
          data: {
            birthday: value.parentBirthday,
          },
        })),
    ])
    const {
      data: { profile },
    } = toolRes

    dispatch({
      type: BABY_VACCINATION_ACTION.CURRENT_BABY,
      payload: selectedBaby ? selectedBaby : profile,
    })

    await dispatch({ type: BABY_GROWTH_ACTION.SUBMIT, payload })

    // push(
    //   queryString.stringifyUrl({
    //     url: `${getHelloSitesUrl(locale)}/${getHealthToolLink(
    //       HEALTH_TOOL_TYPE.BABY_GROWTH,
    //     )}result`,
    //     query: {
    //       joincommunity: 1,
    //     },
    //   }),
    // )

    push(`${asPath.split('?')[0]}/result?joincommunity=1`)
  }

  const onError = (fieldErrors: typeof form.errors) => {
    form.setFieldValue('isSubmitted', true)

    Object.values(fieldErrors).forEach((error) =>
      showNotification({
        type: 'danger',
        message: (error as string) || '',
        title: f({
          id: 'babyGrowth.error.title',
          defaultMessage: 'Invalid data',
        }),
      }),
    )
  }
  const onAddMeasurement = (babyId: number | 'new') => {
    if (babyId === 'new') {
      form.onSubmit(onSubmit, onError)()
      return
    }
    setSelectedBaby(
      () =>
        (babyLists || [])?.find((baby) => Number(baby?.id) === babyId) || null,
    )
  }

  useEffect(() => {
    if (!isLoggedIn) return
    if (isUserReturnFromSSO) {
      const previousData = JSON.parse(
        Cookies.get(TEMP_BABY_GROWTH_KEY) || '{}',
      ) as BabyGrowthFormField

      if (!isEmpty(previousData)) {
        form.setValues({
          gender: previousData.gender,
          height: previousData.height,
          weight: previousData.weight,
          head: previousData.head,
          bmi: previousData.bmi,
          birthday: dayjs(previousData.birthday).toDate(),
          measurementDate: dayjs(previousData.measurementDate).toDate(),
        })
      } else {
        return
      }
    } else {
      // Cookies.remove(TEMP_BABY_GROWTH_KEY)
    }

    if (selectedBaby) {
      const {
        dob,
        gender,
        name,
      } = selectedBaby as BabyVaccinationDetail['profile']
      form.setValues({
        name,
        birthday: dayjs(dob).toDate(),
        gender: generateGender(gender),
        isMyself: true,
      })
      if (isUserReturnFromSSO) form.onSubmit(onSubmit, onError)()
    }
  }, [isLoggedIn, selectedBaby, isUserReturnFromSSO])

  useEffect(() => {
    if (babyListOptions.find((baby) => baby.name === form.values.name)) {
      setIsBlocked(true)
    } else {
      setSelectedBaby(null)
      setIsBlocked(false)
    }
  }, [form.values.name])
  useEffect(() => {
    if (!selectedBaby) return

    if (isLoggedIn) {
      const {
        dob,
        gender,
        name,
        additionalQuestions: babyAdditionalQuestions,
      } = selectedBaby
      const tmp: { [key in string]: string } = {}
      additionalQuestions.map((i) => {
        tmp[ADDITIONAL_QUESTION_PREFIX + i.question.id] = ''
      })
      babyAdditionalQuestions.map((i) => {
        tmp[ADDITIONAL_QUESTION_PREFIX + i.question.id] = (i.answers || [])
          .map((j) => (j.id === 0 ? j.value : j.id))
          .join(',')
      })
      const newFormValues = {
        name,
        birthday: dayjs(dob).toDate(),
        gender: generateGender(gender),
        ...tmp,
      }
      form.setValues(newFormValues)
      setIsBlocked(true)
    } else {
      setIsBlocked(false)
    }
  }, [selectedBaby, isLoggedIn])

  // useEffect(() => {
  //   if (form.values.name !== '' && isUserReturnFromSSO && isLoggedIn) {
  //     form.onSubmit(onSubmit, onError)()
  //   }
  // }, [form.values.name, isUserReturnFromSSO, isLoggedIn])

  return (
    <>
      {isUserReturnFromSSO && isLoggedIn && (
        <BabyGrowthChooseProfileDrawer
          currentProfileId="new"
          babyProfiles={babyLists as any}
          onAddMeasurement={onAddMeasurement as any}
          open={opened}
          onClose={close}
          isMobile={isMobile}
          newBabyValue={form.getInputProps('name').value || ''}
          onNewBabyValueChange={form.getInputProps('name').onChange}
          onSwitchProfile={setSelectedBaby as any}
          // {...form.getInputProps('name')}
        />
      )}
      <StyledBabyGrowthWrapper>
        <StyledBabyGrowthForm onSubmit={form.onSubmit(onSubmit, onError)}>
          {isLoggedIn && (
            <Flex
              gap={rem(24)}
              sx={(theme) => ({
                [theme.fn.smallerThan('sm')]: {
                  flexDirection: 'column',
                },
                [theme.fn.largerThan('sm')]: {
                  '& > div': {
                    width: '100%',
                    flexGrow: 1,
                  },
                },
              })}
            >
              <StyledFormControl>
                <AgeInput {...form.getInputProps('parentBirthday')} />
              </StyledFormControl>
              <StyledFormControl>
                <CheckYourSelfSwitching
                  {...form.getInputProps('isMyself')}
                  checked={form.getInputProps('isMyself').value}
                  withAsterisk
                  label={f({
                    id: 'tools.checkYourKidsInput.label',
                    defaultMessage: 'Are you checking for your kid?',
                  })}
                  sx={(theme) => ({
                    [theme.fn.smallerThan('sm')]: {
                      display: 'flex',
                      justifyContent: 'space-between',
                    },
                  })}
                />
              </StyledFormControl>
            </Flex>
          )}

          {!isUserReturnFromSSO && isLoggedIn && (
            <StyledFormControl>
              <Select
                keepSearch={true}
                label={
                  <Text size="s4" as="label">
                    {f({
                      id: 'babyGrowth.input.babyName',
                      defaultMessage: 'Input baby name or select',
                    })}{' '}
                    <Text as="span" size="n1" color={theme.colors.gray400}>
                      {f({
                        id: 'babyGrowth.optional',
                        defaultMessage: '(optional)',
                      })}
                    </Text>
                  </Text>
                }
                id="baby-name"
                data-testid="name-option"
                placeholder={f({
                  id: 'babyGrowth.placeholder.babyName',
                  defaultMessage: 'Input baby’s name',
                })}
                data={babyListOptions}
                itemComponent={Baby}
                searchable
                onSearchChange={onSearchChange}
                searchValue={searchValue}
                filter={(value: string, baby: BabyOption & SelectItem) => {
                  return ((baby.label as string) || '')
                    .toLowerCase()
                    .includes(value.toLowerCase().trim())
                }}
                onItemSubmit={(item: BabyOption & SelectItem) => {
                  if (typeof item !== 'string') {
                    setSelectedBaby(item)
                  }
                }}
                {...form.getInputProps('name')}
                error={Boolean(form.getInputProps('name').error)}
              />
            </StyledFormControl>
          )}
          <GenderOptionControl
            isBaby
            withAsterisk
            availableValue={isBlocked && form.getInputProps('gender').value}
            genderValue={{
              male: Gender.Boy,
              female: Gender.Girl,
            }}
            {...form.getInputProps('gender')}
          />

          <StyledFormControl>
            <DatePicker
              withAsterisk
              label={
                <Text as="label" size="s4">
                  {f({
                    id: 'babyGrowth.input.birthday',
                    defaultMessage: 'Baby birthday',
                  })}
                </Text>
              }
              {...form.getInputProps('birthday')}
              onChange={
                !isBlocked
                  ? form.getInputProps('birthday').onChange
                  : () => null
              }
              excludeDate={(date) => dayjs(date).isAfter(dayjs())}
              disabled={isBlocked}
              error={Boolean(form.getInputProps('birthday').error)}
            />
          </StyledFormControl>

          <StyledRow>
            <StyledFormControl>
              <Input.Number
                {...form.getInputProps('height')}
                label={
                  <Text size="s4" as="label">
                    {f({
                      id: 'babyGrowth.height',
                      defaultMessage: 'Height',
                    })}{' '}
                    <Text as="span" size="n1" color={theme.colors.gray400}>
                      {f({
                        id: 'babyGrowth.cm',
                        defaultMessage: '(cm)',
                      })}
                    </Text>
                  </Text>
                }
                precision={1}
                inputMode="decimal"
                min={0}
                error={isEmptyThreeFields}
              />
            </StyledFormControl>
            <StyledFormControl>
              <Input.Number
                {...form.getInputProps('weight')}
                label={
                  <Text size="s4" as="label">
                    {f({
                      id: 'babyGrowth.weight',
                      defaultMessage: 'Weight',
                    })}{' '}
                    <Text as="span" size="n1" color={theme.colors.gray400}>
                      {f({
                        id: 'babyGrowth.kg',
                        defaultMessage: 'kg',
                      })}
                    </Text>
                  </Text>
                }
                precision={1}
                inputMode="decimal"
                min={0}
                error={isEmptyThreeFields}
              />
            </StyledFormControl>
          </StyledRow>
          <StyledRow>
            {locale !== LOCALE.Vietnam && (
              <Input.Number
                {...form.getInputProps('head')}
                label={
                  <Text size="s4" as="label">
                    {f({
                      id: 'babyGrowth.headCircumference',
                      defaultMessage: 'Head Circumference',
                    })}{' '}
                    <Text as="span" size="n1" color={theme.colors.gray400}>
                      {f({
                        id: 'babyGrowth.cm',
                        defaultMessage: 'cm',
                      })}
                    </Text>
                  </Text>
                }
                precision={1}
                inputMode="decimal"
                min={0}
                error={isEmptyThreeFields}
              />
            )}
            <Input.Number
              {...form.getInputProps('bmi')}
              label={
                <Text size="s4" as="label">
                  {f({
                    id: 'babyGrowth.input.bmi',
                    defaultMessage: 'Body Mass',
                  })}{' '}
                  <Text as="span" size="n1" color={theme.colors.gray400}>
                    {f({
                      id: 'babyGrowth.kg.m2',
                      defaultMessage: '(kg/m2)',
                    })}
                  </Text>
                </Text>
              }
              precision={1}
              inputMode="decimal"
              min={0}
              disabled
            />
          </StyledRow>
          <StyledFormControl>
            <DatePicker
              withAsterisk
              label={
                <Text as="label" size="s4">
                  {f({
                    id: 'babyGrowth.input.measurementDate',
                    defaultMessage: 'Measurement date',
                  })}
                </Text>
              }
              {...form.getInputProps('measurementDate')}
              // onChange={
              //   !isBlocked
              //     ? form.getInputProps('measurementDate').onChange
              //     : () => null
              // }
              excludeDate={(date) => dayjs(date).isAfter(dayjs())}
              error={Boolean(form.getInputProps('measurementDate').error)}
            />
          </StyledFormControl>
          {additionalQuestions.map((i, idx) => (
            <AdditionalQuestionField
              key={String(idx)}
              question={i}
              multiple={i.isMulti}
              inputProps={form.getInputProps(
                ADDITIONAL_QUESTION_PREFIX + i.question.id,
              )}
              onChange={(v) => {
                form.setFieldValue(
                  ADDITIONAL_QUESTION_PREFIX + i.question.id,
                  v.join(','),
                )
              }}
            />
          ))}
          <Button
            type="submit"
            size="lg"
            variant="primary"
            data-event-category="Health Tool"
            data-event-action="Baby Growth"
            data-event-label={currentUrl}
          >
            {f({
              id: 'babyGrowth.input.submit',
              defaultMessage: "Track your baby's growth",
            })}
          </Button>
        </StyledBabyGrowthForm>
      </StyledBabyGrowthWrapper>
    </>
  )
}

export default BabyGrowthForm
